/**
 * Web Audio API helpers for the Foundation save celebration.
 *
 * Two sounds:
 *   playTaSound()    — short, sharp pre-shot pop (~120ms).
 *   playTammmSound() — long, full-spectrum "BUMMM" main burst (~450ms).
 *
 * Sound preference:
 *   localStorage 'mex_celebration_sound' = 'on' | 'off'. Default 'on'.
 *   Both functions return early when set to 'off'. Visual confetti is
 *   always rendered regardless of audio state.
 *
 * AudioContext is created lazily on first call. Failures (older browsers,
 * autoplay restrictions) are silently swallowed — visual confetti continues.
 */

const STORAGE_KEY = "mex_celebration_sound";

export function isCelebrationSoundOn() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v == null ? true : v !== "off";
  } catch {
    return true;
  }
}

export function setCelebrationSoundOn(on) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {}
}

let _ctx = null;
function getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
    return _ctx;
  } catch {
    return null;
  }
}

/**
 * Pre-warm the AudioContext within a user gesture. Called on the FIRST
 * click/keydown of the page (one-shot, then removed). Eliminates the
 * autoplay-policy `resume()` latency for the very first save chime /
 * cannon sound — critical for new testers' first save experience.
 *
 * Plays a 1-sample silent osc to fully realize the audio graph (so the
 * browser allocates the audio engine resources immediately, not on the
 * first „real" sound 30s later).
 */
let _prewarmed = false;
export function prewarmAudioContext() {
  if (_prewarmed) return;
  const ctx = getCtx();
  if (!ctx) return;
  _prewarmed = true;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.001);
  } catch { /* swallow — pre-warm failures don't break anything */ }
}

if (typeof window !== "undefined") {
  // Egyszeri pre-warm az első user-gesture-re (click vagy keydown).
  // Eltávolítja magát az első érvényes eseménynél, hogy ne fusson
  // többször. NEM blokkolja a UI-t (1ms silent osc).
  const onFirstGesture = () => {
    try { prewarmAudioContext(); } catch { /* noop */ }
    window.removeEventListener("pointerdown", onFirstGesture, true);
    window.removeEventListener("keydown", onFirstGesture, true);
  };
  window.addEventListener("pointerdown", onFirstGesture, { capture: true, once: false });
  window.addEventListener("keydown", onFirstGesture, { capture: true, once: false });
}

function fillNoise(buffer, exponent) {
  const data = buffer.getChannelData(0);
  const len = data.length;
  for (let i = 0; i < len; i++) {
    const decay = Math.pow(1 - i / len, exponent);
    data[i] = (Math.random() * 2 - 1) * decay;
  }
}

/**
 * "Save chime" — 7 ascending sparkle notes + a final lingering high note.
 * Plays synchronously on the "Mentés a Tudástárba" click (user gesture),
 * so the browser autoplay policy permits it. Fully decays in ~1.0s, well
 * before the redirect to the Műhely happens 1.4s later — no overlap with
 * the TA-TA-TAMMM confetti sounds.
 */
export function playSaveChime() {
  if (!isCelebrationSoundOn()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const now = ctx.currentTime;

  function sparkle(freq, startTime, duration, volume) {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(volume, startTime + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration + 0.05);
    } catch {}
  }

  const notes = [880, 1175, 1319, 1568, 1760, 2093, 2349]; // A5 → D7
  notes.forEach((freq, i) => {
    const delay = i * 0.04 + Math.random() * 0.015;
    const dur = 0.4 - i * 0.03;
    const vol = 0.18 - i * 0.012;
    sparkle(freq, now + delay, dur, vol);
  });
  // Final lingering high note (E7).
  sparkle(2637, now + 0.32, 0.7, 0.10);
}

/** Short, sharp "TA" pop (~120ms). Bandpass noise + descending square pulse. */
export function playTaSound() {
  if (!isCelebrationSoundOn()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const now = ctx.currentTime;

  // Bandpass-filtered noise burst (1200Hz center, 150ms duration).
  try {
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.15), ctx.sampleRate);
    fillNoise(buf, 2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = "bandpass";
    filt.frequency.value = 1200;
    filt.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.3, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    src.connect(filt).connect(g).connect(ctx.destination);
    src.start(now);
    src.stop(now + 0.16);
  } catch {}

  // Square-wave pop, 280Hz → 140Hz over 80ms.
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(280, now);
    osc.frequency.exponentialRampToValueAtTime(140, now + 0.08);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.15, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.10);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.10);
  } catch {}
}

/** Long, deep "TAMMM" boom (~450ms). Triple-layer impact: noise + sub + mid. */
export function playTammmSound() {
  if (!isCelebrationSoundOn()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const now = ctx.currentTime;

  // Lowpass-filtered noise body (500Hz cutoff, 500ms).
  try {
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.5), ctx.sampleRate);
    fillNoise(buf, 1.2);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = 500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.65, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    src.connect(filt).connect(g).connect(ctx.destination);
    src.start(now);
    src.stop(now + 0.5);
  } catch {}

  // Sub-bass sweep, 110Hz → 35Hz over 300ms.
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(110, now);
    osc.frequency.exponentialRampToValueAtTime(35, now + 0.30);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.55, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.40);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.40);
  } catch {}

  // Mid-range body, 220Hz → 80Hz over 250ms.
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.25);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.25, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  } catch {}
}
